import { Link } from "react-router-dom";
import { GitBranch, LayoutDashboard, MessageSquare, Server, ArrowRight } from "lucide-react";
import { DocSearch } from "../components/DocSearch";

const products = [
  {
    product: "flows",
    label: "Manage agents (Flows)",
    description:
      "Design, configure, and deploy multi-agent workflows. Build agent graphs, wire connections, attach skills and tools, then run agents via the playground, on a schedule, or through the API.",
    href: "/docs/flows",
    icon: GitBranch,
    color: "text-primary",
    links: [
      { label: "Quickstart", href: "/docs/flows/quickstart" },
      { label: "The agent graph", href: "/docs/flows/graph" },
      { label: "Skills & tools", href: "/docs/flows/skills" },
      { label: "Executions", href: "/docs/flows/executions" },
    ],
  },
  {
    product: "admin",
    label: "Admin dashboard",
    description:
      "Manage everything that supports your agents: model providers, org-level integrations, the shared knowledge base, and team access. One place for workspace-wide configuration.",
    href: "/docs/admin",
    icon: LayoutDashboard,
    color: "text-primary",
    links: [
      { label: "Dashboard", href: "/docs/admin/dashboard" },
      { label: "Integrations", href: "/docs/admin/integrations" },
      { label: "Knowledge Base", href: "/docs/admin/knowledge-base" },
      { label: "Team & roles", href: "/docs/admin/team" },
    ],
  },
  {
    product: "chat",
    label: "Chat",
    description:
      "Talk to your deployed agents in real time. Switch between agents and projects, manage session history, or enter Meta-Agent mode to describe, build, and refine new agents conversationally.",
    href: "/docs/chat",
    icon: MessageSquare,
    color: "text-primary",
    links: [
      { label: "Quickstart", href: "/docs/chat/quickstart" },
      { label: "Meet the Meta-Agent", href: "/docs/chat/meta-agent" },
      { label: "Build an agent", href: "/docs/chat/build-agent" },
      { label: "Slash commands", href: "/docs/chat/slash-commands" },
    ],
  },
  {
    product: "mcp-server",
    label: "Anter MCP Server",
    description:
      "Anter is itself an MCP server. Connect Claude Code, Claude Desktop, or any MCP-compatible client and design, revise, run, and inspect agents conversationally.",
    href: "/docs/mcp-server",
    icon: Server,
    color: "text-primary",
    links: [
      { label: "Quickstart", href: "/docs/mcp-server/quickstart" },
      { label: "Key concepts", href: "/docs/mcp-server/concepts" },
      { label: "Agent lifecycle tools", href: "/docs/mcp-server/agent-lifecycle" },
      { label: "Running & observability", href: "/docs/mcp-server/execution" },
    ],
  },
];

export default function DocsHome() {
  return (
    <div className="max-w-4xl mx-auto pt-6 pb-12 px-6">
      {/* Hero */}
      <div className="mb-6">
        <p className="text-xs text-text-faint mb-2">anter.ai / docs</p>
        <h1 className="text-3xl font-medium text-text mb-2">Anter documentation</h1>
        <p className="text-base text-text-muted mb-5">
          Everything you need to set up, build, and operate Anter.
        </p>
        <DocSearch className="max-w-md" />
      </div>

      {/* Products */}
      <section className="mb-10">
        <h2 className="text-[10px] font-medium tracking-widest uppercase text-text-faint mb-4">
          Products
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {products.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.product}
                className="group flex flex-col rounded-xl border border-border bg-bg-secondary hover:border-primary-border hover:bg-bg-elevated transition-colors"
              >
                {/* Card header — links to section root */}
                <Link to={p.href} className="flex flex-col gap-3 p-5 pb-4">
                  <div
                    className={`w-9 h-9 rounded-lg border border-primary-border bg-primary-dim flex items-center justify-center ${p.color}`}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-text group-hover:text-primary-text transition-colors leading-snug">
                      {p.label}
                    </h3>
                    <ArrowRight
                      size={14}
                      className="text-text-faint group-hover:text-primary mt-0.5 shrink-0 transition-colors"
                    />
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed">{p.description}</p>
                </Link>

                {/* Quick links */}
                <div className="border-t border-border mx-5 mb-4" />
                <div className="px-5 pb-4 flex flex-col gap-0.5">
                  {p.links.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center justify-between text-xs text-text-muted hover:text-primary-text py-1.5 transition-colors group/link"
                    >
                      <span>{link.label}</span>
                      <ArrowRight
                        size={11}
                        className="text-text-faint group-hover/link:text-primary transition-colors shrink-0"
                      />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Getting started */}
      <section>
        <h2 className="text-[10px] font-medium tracking-widest uppercase text-text-faint mb-4">
          Getting started
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              step: 1,
              title: "Add a model provider",
              desc: "Connect your LLM API key in Admin → Model Providers.",
              href: "/docs/admin/dashboard",
            },
            {
              step: 2,
              title: "Build your first flow",
              desc: "Create a project and wire an agent graph in Flows.",
              href: "/docs/flows/quickstart",
            },
            {
              step: 3,
              title: "Test in the playground",
              desc: "Run and debug your agent before going live.",
              href: "/docs/flows/playground",
            },
            {
              step: 4,
              title: "Chat with your agent",
              desc: "Open Chat and select your new project and agent.",
              href: "/docs/chat/quickstart",
            },
          ].map((s) => (
            <Link
              key={s.step}
              to={s.href}
              className="group bg-bg-secondary border border-border rounded-xl p-4 hover:bg-bg-elevated hover:border-primary-border transition-colors"
            >
              <div className="w-6 h-6 rounded-full bg-primary-dim border border-primary-border flex items-center justify-center mb-3">
                <span className="text-xs font-medium text-primary-text">{s.step}</span>
              </div>
              <h3 className="text-xs font-semibold text-text mb-1 group-hover:text-primary-text transition-colors">
                {s.title}
              </h3>
              <p className="text-xs text-text-muted leading-relaxed">{s.desc}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
